import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDateRange } from '../lib/dateUtils';
import { t } from '../lib/i18n';
import './MemoriesPage.css';

const PERIODS = [
  { label: '1주일 전 오늘',  days: 7 },
  { label: '한 달 전 오늘',  months: 1 },
  { label: '3개월 전 오늘',  months: 3 },
  { label: '6개월 전 오늘',  months: 6 },
  { label: '1년 전 오늘',    years: 1 },
  { label: '2년 전 오늘',    years: 2 },
  { label: '3년 전 오늘',    years: 3 },
];

function getTargetDate({ days, months, years }) {
  const d = new Date();
  if (days)   d.setDate(d.getDate() - days);
  if (months) d.setMonth(d.getMonth() - months);
  if (years)  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
}

function shiftDate(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatKorDate(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return `${y}년 ${parseInt(m)}월 ${parseInt(d)}일`;
}

// 시작일부터 오늘까지 몇 일째 (시작일 = 1일째)
function calcDays(startDateStr) {
  const start = new Date(startDateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - start) / 86400000) + 1;
}

// 한국어 조사 "와/과" 판별
function josaWaGwa(str) {
  if (!str) return '와';
  const last = str[str.length - 1];
  const code = last.charCodeAt(0);
  if (code >= 0xAC00 && code <= 0xD7A3) {
    return (code - 0xAC00) % 28 > 0 ? '과' : '와';
  }
  return '와';
}

export default function MemoriesPage({ user, onNavigate, refreshSignal }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('records')
        .select('*')
        .eq('user_id', user.id);
      setRecords(data || []);
      setLoading(false);
    };
    fetch();
  }, [user.id, refreshSignal]);

  // 진행 중인 기록의 기념일
  const anniversaries = records
    .filter((r) => r.is_ongoing && r.start_date)
    .map((r) => ({ ...r, days: calcDays(r.start_date) }))
    .sort((a, b) => b.days - a.days);

  // 기간별 섹션
  const sections = PERIODS.map((period) => {
    const targetDate = getTargetDate(period);
    const from = shiftDate(targetDate, -3);
    const to   = shiftDate(targetDate,  3);
    const matched = records
      .filter((r) => r.start_date >= from && r.start_date <= to)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return { ...period, targetDate, matched };
  }).filter((s) => s.matched.length > 0);

  // 연도 목록 (내림차순)
  const years = [...new Set(
    records.filter((r) => r.start_date).map((r) => parseInt(r.start_date.slice(0, 4)))
  )].sort((a, b) => b - a);

  // 선택된 연도의 기록 → 월별 그룹
  const yearRecords = selectedYear
    ? records
        .filter((r) => r.start_date && r.start_date.startsWith(String(selectedYear)))
        .sort((a, b) => b.start_date.localeCompare(a.start_date))
    : [];

  const monthSections = selectedYear
    ? [...new Set(yearRecords.map((r) => parseInt(r.start_date.slice(5, 7))))]
        .sort((a, b) => b - a)
        .map((month) => ({
          month,
          records: yearRecords.filter(
            (r) => parseInt(r.start_date.slice(5, 7)) === month
          ),
        }))
        .filter((s) => s.records.length > 0)
    : [];

  const hasContent = anniversaries.length > 0 || sections.length > 0;

  return (
    <div className="memories-page">
      <header className="memories-header">
        <span className="memories-title">{t('memories_title')}</span>
      </header>

      <div className="memories-content">
        {loading && <p className="memories-empty">{t('home_loading')}</p>}

        {/* 연도 필터 버튼 */}
        {!loading && years.length > 0 && (
          <div className="year-filter">
            {years.map((year) => (
              <button
                key={year}
                className={`year-btn${selectedYear === year ? ' year-btn--active' : ''}`}
                onClick={() => setSelectedYear(selectedYear === year ? null : year)}
              >
                {year}{t('year')} {t('memories_year_memories')}
              </button>
            ))}
          </div>
        )}

        {/* ===== 연도별 뷰 ===== */}
        {!loading && selectedYear && (
          <>
            <p className="year-summary">{selectedYear}{t('year')} {t('memories_year_records')} {yearRecords.length}{t('count_unit')}</p>
            {yearRecords.length === 0 && (
              <div className="memories-empty">
                <p className="memories-empty-icon">📭</p>
                <p>{selectedYear}년에는 기록이 없어요.</p>
              </div>
            )}
            {monthSections.map((section) => (
              <div key={section.month} className="memories-section">
                <div className="memories-section-header">
                  <span className="memories-period">{section.month}월</span>
                  <span className="memories-date">{section.records.length}{t('count_unit')}</span>
                </div>
                {section.records.map((record) => (
                  <div
                    key={record.id}
                    className="memory-card"
                    role="button"
                    onClick={() => onNavigate('editRecord', record)}
                  >
                    <div className="memory-card-left">
                      <span className="memory-emoji">{record.category_emoji ?? '📌'}</span>
                    </div>
                    <div className="memory-card-body">
                      <p className="memory-title">{record.title}</p>
                      {record.category_name && (
                        <span className="memory-category">{record.category_name}</span>
                      )}
                      <p className="memory-meta">
                        {formatDateRange(record.start_date, record.end_date, record.is_ongoing)}
                      </p>
                      {record.memo && <p className="memory-memo">{record.memo}</p>}
                    </div>
                    {record.photos?.length > 0 && (
                      <div className="memory-thumbnail">
                        <img src={record.photos[0]} alt="썸네일" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        {/* ===== 기본 뷰 (연도 미선택 시) ===== */}
        {!loading && !selectedYear && !hasContent && (
          <div className="memories-empty">
            <p className="memories-empty-icon">🗓️</p>
            <p>{t('memories_empty')}</p>
          </div>
        )}

        {!loading && !selectedYear && anniversaries.length > 0 && (
          <div className="anniversary-section">
            <p className="anniversary-section-label">🎉 {t('memories_anniversary')}</p>
            {anniversaries.map((r) => (
              <div
                key={r.id}
                className="anniversary-card"
                role="button"
                onClick={() => onNavigate('editRecord', r)}
              >
                <span className="anniversary-emoji">{r.category_emoji ?? '📌'}</span>
                <div className="anniversary-text">
                  <p className="anniversary-message">
                    {r.title}{josaWaGwa(r.title)} <strong>{r.days}일째</strong>예요!
                  </p>
                  <p className="anniversary-sub">{formatKorDate(r.start_date)} 시작</p>
                </div>
                <span className="anniversary-days">D+{r.days}</span>
              </div>
            ))}
          </div>
        )}

        {!loading && !selectedYear && sections.map((section) => (
          <div key={section.label} className="memories-section">
            <div className="memories-section-header">
              <span className="memories-period">{section.label}</span>
              <span className="memories-date">{formatKorDate(section.targetDate)}</span>
            </div>

            {section.matched.map((record) => (
              <div
                key={record.id}
                className="memory-card"
                role="button"
                onClick={() => onNavigate('editRecord', record)}
              >
                <div className="memory-card-left">
                  <span className="memory-emoji">{record.category_emoji ?? '📌'}</span>
                </div>
                <div className="memory-card-body">
                  <p className="memory-title">{record.title}</p>
                  {record.category_name && (
                    <span className="memory-category">{record.category_name}</span>
                  )}
                  <p className="memory-meta">
                    {formatDateRange(record.start_date, record.end_date, record.is_ongoing)}
                  </p>
                  {record.memo && <p className="memory-memo">{record.memo}</p>}
                </div>
                {record.photos?.length > 0 && (
                  <div className="memory-thumbnail">
                    <img src={record.photos[0]} alt="썸네일" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
